import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const ValetCheckIn = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    
    const [lotInfo, setLotInfo] = useState(null);
    const [availableSpaces, setAvailableSpaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    // Form State
    const [selectedSpace, setSelectedSpace] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [plateNumber, setPlateNumber] = useState('');
    
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        const fetchLotData = async () => {
            try {
                // Fetch lots for the user's organization
                const lotsRes = await axios.get('/lots');
                const targetLot = lotsRes.data.find(l => l.organization_id === user.organization_id) || lotsRes.data[0];
                
                if (!targetLot) {
                    throw new Error("No lot found for your organization.");
                }

                if (targetLot.is_paid_lot) {
                    throw new Error("Valet mode is not available for paid lots.");
                }

                setLotInfo(targetLot);

                // Fetch spaces for this lot
                const spacesRes = await axios.get(`/lots/${targetLot.id}/spaces`);
                
                // spacesRes.data is grouped by zone (group_name)
                // Flatten and filter for GREEN status
                const flatSpaces = Object.entries(spacesRes.data)
                    .flatMap(([zone, spaces]) => spaces.map(s => ({ ...s, group_name: zone })))
                    .filter(space => space.status === 'GREEN')
                    .sort((a, b) => {
                        if (a.group_name < b.group_name) return -1;
                        if (a.group_name > b.group_name) return 1;
                        return String(a.space_number).localeCompare(String(b.space_number), undefined, { numeric: true });
                    });
                
                setAvailableSpaces(flatSpaces);
                setLoading(false);
            } catch (err) {
                setError(err.message || err.response?.data?.detail || "Could not load lot information.");
                setLoading(false);
            }
        };
        fetchLotData();
    }, [user.organization_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        setError('');
        
        try {
            await axios.post(`/spaces/valet-checkin`, {
                space_id: parseInt(selectedSpace),
                plate_number: plateNumber,
                phone_number: phoneNumber
            });
            
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Check-in failed. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-color)' }}>Loading...</div>;
    
    if (error && !lotInfo) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
            <div className="glass-card" style={{ padding: '30px', textAlign: 'center', color: 'var(--danger)', maxWidth: '400px' }}>
                <h3>Error</h3>
                <p>{error}</p>
                <button onClick={() => navigate('/')} className="btn" style={{ marginTop: '15px', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}>Back to Dashboard</button>
            </div>
        </div>
    );

    if (success) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px', background: 'var(--bg-color)' }}>
            <div className="glass-card" style={{ padding: '40px', textAlign: 'center', maxWidth: '400px', width: '100%' }}>
                <div style={{ fontSize: '48px', color: 'var(--success)', marginBottom: '20px' }}>✓</div>
                <h2 style={{ color: 'var(--text-color)', marginBottom: '10px' }}>Check-In Successful!</h2>
                <p style={{ color: 'var(--text-color)', marginBottom: '20px' }}>The vehicle has been checked in.</p>
                <div style={{ padding: '15px', background: 'var(--input-bg)', borderRadius: '8px', marginBottom: '20px', border: '1px solid var(--input-border)' }}>
                    <p style={{ margin: '5px 0', color: 'var(--text-color)' }}><strong>Lot:</strong> {lotInfo.lot_name}</p>
                    <p style={{ margin: '5px 0', color: 'var(--text-color)' }}><strong>Space:</strong> {availableSpaces.find(s => s.id === parseInt(selectedSpace))?.space_number}</p>
                    <p style={{ margin: '5px 0', color: 'var(--text-color)' }}><strong>License Plate:</strong> {plateNumber}</p>
                </div>
                <button onClick={() => navigate('/')} className="btn btn-primary" style={{ width: '100%', padding: '12px', fontSize: '16px' }}>Return to Dashboard</button>
                <button 
                    onClick={() => {
                        setSuccess(false);
                        setSelectedSpace('');
                        setPlateNumber('');
                        setPhoneNumber('');
                        // Refetch spaces to ensure accurate availability
                        setLoading(true);
                        axios.get(`/lots/${lotInfo.id}/spaces`).then(res => {
                            const flatSpaces = Object.entries(res.data)
                                .flatMap(([zone, spaces]) => spaces.map(s => ({ ...s, group_name: zone })))
                                .filter(space => space.status === 'GREEN')
                                .sort((a, b) => {
                                    if (a.group_name < b.group_name) return -1;
                                    if (a.group_name > b.group_name) return 1;
                                    return String(a.space_number).localeCompare(String(b.space_number), undefined, { numeric: true });
                                });
                            setAvailableSpaces(flatSpaces);
                            setLoading(false);
                        });
                    }} 
                    className="btn" 
                    style={{ width: '100%', padding: '12px', fontSize: '16px', marginTop: '10px', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}
                >
                    Check In Another
                </button>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px', background: 'var(--bg-color)' }}>
            <div className="glass-card" style={{ padding: '30px', maxWidth: '400px', width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <img src="/logo-primary.png" alt="Logo" style={{ height: '40px', marginBottom: '15px' }} />
                    <h2 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>Valet Check-In</h2>
                    <p style={{ color: 'var(--text-color)', opacity: 0.8 }}>{lotInfo.lot_name}</p>
                </div>

                {error && <div style={{ color: 'white', backgroundColor: 'var(--danger-hover)', padding: '10px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center' }}>{error}</div>}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>Select Space</label>
                        <select 
                            value={selectedSpace} 
                            onChange={(e) => setSelectedSpace(e.target.value)} 
                            required
                            style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                        >
                            <option value="" disabled>Choose an available space...</option>
                            {availableSpaces.map(space => (
                                <option key={space.id} value={space.id}>
                                    {space.space_number} (Zone: {space.group_name})
                                </option>
                            ))}
                        </select>
                        {availableSpaces.length === 0 && <span style={{ color: 'var(--danger)', fontSize: '12px' }}>No spaces available in this lot!</span>}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>License Plate</label>
                        <input 
                            type="text" 
                            placeholder="e.g. ABC-1234" 
                            value={plateNumber} 
                            onChange={(e) => setPlateNumber(e.target.value.toUpperCase())} 
                            required
                            style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>Driver's Phone Number</label>
                        <input 
                            type="tel" 
                            placeholder="(555) 555-5555" 
                            value={phoneNumber} 
                            onChange={(e) => setPhoneNumber(e.target.value)} 
                            required
                            style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                        />
                    </div>

                    <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isProcessing || availableSpaces.length === 0}
                        style={{ marginTop: '10px', padding: '15px', fontSize: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        {isProcessing ? <span>Processing...</span> : <span>Complete Check-In</span>}
                    </button>
                    
                    <button 
                        type="button" 
                        onClick={() => navigate('/')} 
                        className="btn" 
                        style={{ padding: '15px', fontSize: '16px', backgroundColor: 'var(--input-border)', color: 'var(--text-color)' }}
                    >
                        Cancel
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ValetCheckIn;
